import { motion } from "framer-motion";

import pinna from "../../accests/pinna/pinna (1).jpeg"
import pinna1 from "../../accests/pinna/pinna (2).jpeg"
import pinna2 from "../../accests/pinna/pinna (3).jpeg"
import pinna3 from "../../accests/pinna/pinna (4).jpeg"
import pinna4 from "../../accests/pinna/pinna (5).jpeg"
import pinna5 from "../../accests/pinna/pinna9.jpeg"

// Unsplash / example images
const heroImg =
    pinna4; // Pinnawala Elephant


export default function PinnawalaElephantTour() {
    const highlights = [
        {
            title: "Pinnawala Elephant Orphanage",
            desc: "See orphaned, sick, and injured elephants being cared for, a rare opportunity to observe these majestic creatures up close.",
            img: pinna4,
        },
        {
            title: "Elephant Bathing",
            desc: "Witness elephants bathing and playing in the river — a spectacular sight and perfect photo opportunity.",
            img: pinna1,
        },
        {
            title: "Feeding Time",
            desc: "Experience the joy of feeding an elephant, an unforgettable interactive experience.",
            img: pinna3,
        },
        {
            title: "Herbal & Spice Garden",
            desc: "Visit a local herbal and spice garden to learn about traditional Sri Lankan remedies.",
            img: pinna5,
        },
    ];

    const itinerary = [
        "Morning pickup from your hotel",
        "Arrive at Pinnawala Elephant Orphanage",
        "Observe and feed the elephants",
        "Watch elephants bathe and play in the river",
        "Visit the herbal and spice garden",
        "Enjoy lunch/snacks by the river (optional)",
        "Return to your hotel",
    ];

    const travelTips = [
        "Unlimited mineral water is provided",
        "Bring a camera to capture unforgettable moments",
        "There’s a great riverside restaurant for snacks or lunch",
        "Children must be accompanied by an adult",
        "Pregnant women and people with back/heart issues should avoid the tour",
    ];

    return (
        <div className="pt-28 bg-gray-50">

            {/* HERO */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1 }}
                className="relative h-[65vh]"
            >
                <img
                    src={heroImg}
                    alt="Pinnawala Elephant Orphanage Tour"
                    className="w-full h-full object-cover brightness-75"
                />
                <div className="absolute inset-0 flex flex-col justify-center items-center text-center text-white px-6">
                    <motion.h1
                        initial={{ y: -40, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 1, delay: 0.3 }}
                        className="text-5xl md:text-6xl font-bold drop-shadow-lg"
                    >
                        Pinnawala Elephant Orphanage Day Tour
                    </motion.h1>
                    <motion.p
                        initial={{ y: 40, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 1, delay: 0.6 }}
                        className="mt-4 max-w-3xl text-lg drop-shadow-md"
                    >
                        Experience Sri Lanka’s majestic elephants up close, watch them bathe in the river,
                        and explore a traditional herbal and spice garden.
                    </motion.p>
                </div>
            </motion.div>

            {/* HIGHLIGHTS */}
            <section className="max-w-7xl mx-auto px-6 py-16">
                <h2 className="text-3xl font-bold text-center mb-12 text-gray-800">
                    Highlights
                </h2>
                <div className="grid md:grid-cols-4 gap-10">
                    {highlights.map((item, i) => (
                        <motion.div
                            key={i}
                            whileHover={{ scale: 1.05 }}
                            className="bg-white rounded-3xl shadow-lg overflow-hidden transition"
                        >
                            <img
                                src={item.img}
                                alt={item.title}
                                className="w-full h-64 object-cover"
                            />
                            <div className="p-6">
                                <h3 className="text-2xl font-semibold text-yellow-500 mb-2">
                                    {item.title}
                                </h3>
                                <p className="text-gray-600 text-sm">{item.desc}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* ITINERARY */}
            <section className="bg-gray-100 py-16">
                <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-start">
                    {/* LEFT – ITINERARY LIST */}
                    <div>
                        <h2 className="text-3xl font-bold mb-10 text-gray-800">Detailed Itinerary</h2>
                        <ul className="space-y-4 text-gray-700 list-decimal list-inside">
                            {itinerary.map((step, index) => (
                                <motion.li
                                    key={index}
                                    initial={{ x: -50, opacity: 0 }}
                                    whileInView={{ x: 0, opacity: 1 }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    className="text-lg"
                                >
                                    {step}
                                </motion.li>
                            ))}
                        </ul>
                    </div>

                    {/* RIGHT – SMALL IMAGES */}
                    <div className="grid grid-cols-2 gap-4">
                        {[pinna, pinna1, pinna3, pinna4].map((img, i) => (
                            <motion.img
                                key={i}
                                src={img}
                                alt="Tour spot"
                                whileHover={{ scale: 1.05 }}
                                className="h-40 w-full object-cover rounded-2xl shadow-lg"
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* WHY CHOOSE THIS TOUR + CTA */}
            <section className="relative py-24 overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src={pinna5}
                        alt="Why choose Pinnawala tour"
                        className="w-full h-full object-cover scale-110 blur-md"
                    />
                    <div className="absolute inset-0 bg-black/60"></div>
                </div>

                <div className="relative max-w-6xl mx-auto px-6 text-white text-center">
                    <h2 className="text-3xl md:text-4xl font-bold mb-10">
                        Why Choose This Tour?
                    </h2>

                    <ul className="max-w-3xl mx-auto space-y-4 text-lg list-disc list-inside backdrop-blur-md bg-white/10 p-8 rounded-3xl shadow-xl">
                        <li>Visit the world-renowned Pinnawala Elephant Orphanage</li>
                        <li>Get a chance to feed the elephants</li>
                        <li>Watch elephants bathe and play in the river</li>
                        <li>Visit a traditional herbal and spice garden</li>
                        <li>Personalized private tour with expert guide</li>
                    </ul>

                    {/* CTA */}
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="mt-14"
                    >
                        <motion.a
                            whileHover={{ scale: 1.08 }}
                            whileTap={{ scale: 0.95 }}
                            href="https://wa.me/94770163670?text=Hello%20I%20would%20like%20to%20book%20the%20Pinnawala%20Elephant%20Tour"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center px-14 py-4
        bg-yellow-500 text-black font-semibold text-lg
        rounded-full shadow-xl hover:bg-yellow-400
        transition-all duration-300"
                        >
                            Book Pinnawala Elephant Tour
                        </motion.a>

                        <p className="mt-4 text-sm text-gray-200">
                            📞 Custom tours • 🕒 Flexible timing • 🚗 Comfortable transport
                        </p>
                    </motion.div>

                </div>
            </section>

            {/*/!* TRAVEL TIPS *!/*/}
            {/*<section className="bg-gray-50 py-20">*/}
            {/*    <div className="max-w-6xl mx-auto px-6 text-center">*/}
            {/*        <h2 className="text-3xl md:text-4xl font-bold mb-12 text-gray-800">*/}
            {/*            Travel Tips*/}
            {/*        </h2>*/}

            {/*        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">*/}
            {/*            {travelTips.map((tip, i) => (*/}
            {/*                <motion.div*/}
            {/*                    key={i}*/}
            {/*                    whileHover={{ scale: 1.05 }}*/}
            {/*                    className="bg-white p-6 rounded-3xl shadow-lg border border-gray-200 transition"*/}
            {/*                >*/}
            {/*                    <h3 className="text-xl font-semibold mb-2 text-gray-800">*/}
            {/*                        {tip.split("•")[0]}*/}
            {/*                    </h3>*/}
            {/*                    <p className="text-gray-600 text-sm">{tip}</p>*/}
            {/*                </motion.div>*/}
            {/*            ))}*/}
            {/*        </div>*/}
            {/*    </div>*/}
            {/*</section>*/}

            {/*/!* BOOK CTA *!/*/}
            {/*<section className="text-center py-16">*/}
            {/*    <motion.a*/}
            {/*        whileHover={{ scale: 1.05 }}*/}
            {/*        href="/contact"*/}
            {/*        className="inline-block px-12 py-4 bg-yellow-500 text-white font-semibold rounded-full shadow-lg hover:bg-yellow-600 transition"*/}
            {/*    >*/}
            {/*        Book Pinnawala Elephant Orphanage Day Tour*/}
            {/*    </motion.a>*/}
            {/*</section>*/}
        </div>
    );
}
